import { CheckCircle2, Printer, RefreshCw, Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { PrinterStatus } from "../types";

export function ThermalPrinterPanel({
  unitId,
  stockNumber,
  canPrint,
  onSuccess,
  onError
}: {
  unitId: string;
  stockNumber: string;
  canPrint: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [printer, setPrinter] = useState<PrinterStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      setPrinter(await api.printerStatus());
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : "Could not check the printer");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function print() {
    setPrinting(true);
    try {
      const result = await api.printInventoryLabel(unitId);
      onSuccess(`${stockNumber}: ${result.message}`);
      await loadStatus();
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : "The label could not be sent");
      await loadStatus();
    } finally {
      setPrinting(false);
    }
  }

  const browserPrinting = printer?.configuration.mode === "browser_pdf";
  const remoteBridge = printer?.configuration.mode === "remote_bridge";
  const printReadyPdf = `/api/printer/inventory/${encodeURIComponent(unitId)}/print.pdf`;

  return (
    <section className="thermal-label-panel">
      <div className="thermal-label-summary">
        <Printer />
        <span>
          <strong>D450 wireless tote label</strong>
          <small>
            100 × 150 mm portrait · upright stock number, tote name, NFC QR code,
            25 mm NFC placement area, phone “Scan Me” prompt, destination, and warnings.
          </small>
          <em className={printer?.ready ? "printer-state ready" : "printer-state"}>
            {printer?.ready ? <CheckCircle2 /> : <RefreshCw />}
            {loading ? "Checking printer…" : printer?.message || "Printer status unavailable"}
          </em>
        </span>
      </div>
      <div className="thermal-label-actions">
        {canPrint && browserPrinting && (
          <a
            className="button primary print-now-button"
            href={printReadyPdf}
            target="_blank"
            rel="noreferrer"
          >
            <Printer />
            Open print-ready label
          </a>
        )}
        {canPrint && !browserPrinting && (
          <button
            className="button primary print-now-button"
            onClick={print}
            disabled={printing || loading || !printer?.ready}
          >
            <Printer />
            {printing ? "Sending…" : "Print tote label"}
          </button>
        )}
        {!browserPrinting && !remoteBridge && !printer?.ready && canPrint && (
          <Link className="button secondary" to="/settings">
            <Settings />
            Connect printer
          </Link>
        )}
        <a
          className="button secondary"
          href={`/api/printer/inventory/${encodeURIComponent(unitId)}/preview.png`}
          target="_blank"
          rel="noreferrer"
        >
          Preview 100 × 150 portrait
        </a>
        {browserPrinting && (
          <small className="thermal-print-help">
            In the PDF viewer choose Print, select the D450, use 100 × 150 mm portrait,
            and keep scaling at 100%.
          </small>
        )}
        <a
          className="button quiet"
          href={`/api/inventory/${encodeURIComponent(unitId)}/thermal-label.pdf`}
          target="_blank"
          rel="noreferrer"
        >
          Open privacy-only PDF
        </a>
      </div>
    </section>
  );
}
